import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../db/engine';
import { MARCA_NO_RENUEVA } from '../../db/renovaciones';
import { FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { fmtFecha } from '../../utils/fecha';
import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { loadXLSX } from '../../utils/xlsx';

// ============================================================================
// Renovaciones — avance del período (submódulo de Pólizas).
//
// % de renovación = renovadas ÷ total que vencen en el período ("avance"),
// SIEMPRE sobre TU cartera:
//   - Agente     → tu cartera de agente (lo scopea el RLS).
//   - Ejecutivo  → las pólizas donde eres ejecutivo de cuenta (`polizas.ejecutivo_id`).
//   - Master     → todo; puede filtrar por agente o por ejecutivo para ver cuánto
//                  le falta gestionar a cada uno.
//
// Clasificación de cada póliza que vence en el período:
//   - renovada    → estado 'renovada', otra póliza la renueva (renovacion_de_poliza_id /
//                   poliza_anterior_id) o su solicitud de renovación fue 'convertida'.
//   - no_renovada → venció sin renovarse ('vencida'), su solicitud quedó rechazada/cancelada,
//                   o su tarea del CRM se cerró como "Cliente no renueva" (para ESA vigencia).
//   - pendiente   → el resto. Dentro de pendiente, "en gestión" = la tarea de renovación
//                   del CRM ya fue trabajada (pospuesta, reasignada o con historial) aunque
//                   no esté cerrada. Sirve para ver si el ejecutivo ya gestionó al cliente.
// ============================================================================

// Carga la librería de Excel bajo demanda (mismo patrón que el resto de los módulos).

const CLASES = {
  renovada:    { label: 'Renovada',              color: 'var(--text-success)', bg: 'rgba(34, 197, 94, 0.12)' },
  pendiente:   { label: 'Pendiente por renovar', color: 'var(--text-warning)', bg: 'rgba(245, 158, 11, 0.12)' },
  no_renovada: { label: 'No renovada',           color: 'var(--text-danger)', bg: 'rgba(239, 68, 68, 0.12)' },
};

// Rango del mes en curso (desde el día 1 hasta el último día), en fechas locales.
const rangoMesActual = () => {
  const d = new Date();
  const y = d.getFullYear(), m = d.getMonth();
  const p2 = (n) => String(n).padStart(2, '0');
  const ultimo = new Date(y, m + 1, 0).getDate();
  return { desde: `${y}-${p2(m + 1)}-01`, hasta: `${y}-${p2(m + 1)}-${p2(ultimo)}` };
};

export default function RenovacionesView({ aseguradoras = [], ramos = [], ejecutivos = [], agentes = [], onVerPoliza }) {
  const { user, esAgenteRestringido, esEjecutivo } = useApp();
  const puedeFiltrarCartera = !!user?.es_master || (!esAgenteRestringido && !esEjecutivo);

  const rango0 = rangoMesActual();
  const [desde, setDesde]       = useState(rango0.desde);
  const [hasta, setHasta]       = useState(rango0.hasta);
  const [loading, setLoading]   = useState(false);
  const [rows, setRows]         = useState([]);
  const [fClase, setFClase]     = useState('');   // ''|renovada|pendiente|en_gestion|sin_gestionar|no_renovada
  const [fAseg, setFAseg]       = useState('');
  const [fRamo, setFRamo]       = useState('');
  const [fEjec, setFEjec]       = useState('');   // scope master: por ejecutivo de cuenta
  const [fAgente, setFAgente]   = useState('');   // scope master: por agente

  const load = useCallback(async () => {
    if (!desde || !hasta) return;
    setLoading(true);
    try {
      // Denominador: las que vencen en el período. El scope por cartera:
      //  - agente  → lo aplica el RLS (solo ve su cartera).
      //  - ejecutivo → filtramos por su ejecutivo_id (el RLS no lo limita).
      //  - master  → todo (y filtra por agente/ejecutivo en la UI).
      let q = supabase.from('polizas')
        .select('id, numero, fecha_inicio, fecha_vencimiento, estado, prima, cliente_id, clientes(nombre), ramo_id, ramos(nombre), aseguradora_id, aseguradoras(nombre), ejecutivo_id, ejecutivos!polizas_ejecutivo_id_fkey(nombre), poliza_agentes(agente_id, agentes(nombre))')
        .gte('fecha_vencimiento', desde).lte('fecha_vencimiento', hasta)
        .in('estado', ['activa', 'en_renovacion', 'vencida', 'renovada', 'no_renovada']);
      if (esEjecutivo && user?.ejecutivo_id) q = q.eq('ejecutivo_id', user.ejecutivo_id);
      const { data: venc } = await q;
      const base = venc || [];
      const ids = base.map(p => p.id);

      const renovadaSet = new Set(), convertidaSet = new Set(), declinadaSet = new Set();
      const gestionadaPol = new Set();
      if (ids.length) {
        const [{ data: apuntan }, { data: sols }, { data: tareas }] = await Promise.all([
          supabase.from('polizas').select('renovacion_de_poliza_id, poliza_anterior_id')
            .or(`renovacion_de_poliza_id.in.(${ids.join(',')}),poliza_anterior_id.in.(${ids.join(',')})`),
          supabase.from('solicitudes').select('poliza_endoso_id, estado')
            .eq('tipo', 'renovacion').in('poliza_endoso_id', ids),
          supabase.from('crm_gestiones').select('id, poliza_id, posponer_hasta, estado, fecha_vencimiento')
            .eq('tipo', 'renovacion').in('poliza_id', ids),
        ]);
        (apuntan || []).forEach(p => {
          if (p.renovacion_de_poliza_id) renovadaSet.add(p.renovacion_de_poliza_id);
          if (p.poliza_anterior_id) renovadaSet.add(p.poliza_anterior_id);
        });
        (sols || []).forEach(s => {
          if (s.estado === 'convertida') convertidaSet.add(s.poliza_endoso_id);
          else if (['rechazada', 'cancelada'].includes(s.estado)) declinadaSet.add(s.poliza_endoso_id);
        });
        // "En gestión": la tarea de renovación fue trabajada (pospuesta, en proceso o con historial).
        const tareasArr = tareas || [];
        const taskIds = tareasArr.map(t => t.id);
        let histSet = new Set();
        const noRenuevaTask = new Set();
        if (taskIds.length) {
          const { data: hist } = await supabase.from('crm_historial').select('gestion_id, accion, detalle').in('gestion_id', taskIds);
          histSet = new Set((hist || []).map(h => h.gestion_id));
          // "Cliente no renueva" = decisión tomada a mano al cerrar la tarea (CRM handleCerrarConMotivo).
          (hist || []).forEach(h => {
            if (h.accion === 'cerrar' && String(h.detalle || '').startsWith(MARCA_NO_RENUEVA)) noRenuevaTask.add(h.gestion_id);
          });
        }
        const fvPol = {};
        base.forEach(p => { fvPol[p.id] = String(p.fecha_vencimiento || '').slice(0, 10); });
        tareasArr.forEach(t => {
          if (t.posponer_hasta || t.estado === 'en_proceso' || histSet.has(t.id)) gestionadaPol.add(t.poliza_id);
          // Solo cuenta para la vigencia en que se decidió (mismo vencimiento que la póliza).
          if (noRenuevaTask.has(t.id) && t.fecha_vencimiento &&
              String(t.fecha_vencimiento).slice(0, 10) === fvPol[t.poliza_id]) declinadaSet.add(t.poliza_id);
        });
      }

      const clasificar = (p) => {
        if (p.estado === 'renovada' || renovadaSet.has(p.id) || convertidaSet.has(p.id)) return 'renovada';
        if (p.estado === 'vencida' || p.estado === 'no_renovada' || declinadaSet.has(p.id)) return 'no_renovada';
        return 'pendiente';
      };

      setRows(base.map(p => ({
        ...p,
        cliente_nombre: p.clientes?.nombre || '',
        ramo_nombre: p.ramos?.nombre || '',
        aseguradora_nombre: p.aseguradoras?.nombre || '',
        ejecutivo_nombre: p.ejecutivos?.nombre || '',
        agente_ids: (p.poliza_agentes || []).map(a => a.agente_id),
        agente_nombres: (p.poliza_agentes || []).map(a => a.agentes?.nombre).filter(Boolean).join(', '),
        clase: clasificar(p),
        gestionada: gestionadaPol.has(p.id),
      })));
    } catch (e) {
      alert('Error al cargar renovaciones: ' + (e.message || ''));
    } finally { setLoading(false); }
  }, [desde, hasta, esEjecutivo, user?.ejecutivo_id]);

  useEffect(() => { load(); }, [load]);

  // Filtros de análisis (aseguradora/ramo) + scope master (agente/ejecutivo). NO incluye el de clase.
  const conFiltros = useMemo(() => rows.filter(r =>
    (!fAseg || String(r.aseguradora_id) === String(fAseg)) &&
    (!fRamo || String(r.ramo_id) === String(fRamo)) &&
    (!fEjec || String(r.ejecutivo_id) === String(fEjec)) &&
    (!fAgente || r.agente_ids.some(id => String(id) === String(fAgente)))
  ), [rows, fAseg, fRamo, fEjec, fAgente]);

  const tot     = conFiltros.length;
  const nRenov  = conFiltros.filter(r => r.clase === 'renovada').length;
  const pend    = conFiltros.filter(r => r.clase === 'pendiente');
  const nPend   = pend.length;
  const nGest   = pend.filter(r => r.gestionada).length;
  const nSinGest = nPend - nGest;
  const nNo     = conFiltros.filter(r => r.clase === 'no_renovada').length;
  const pct     = tot ? Math.round((nRenov / tot) * 100) : 0;

  const visibles = useMemo(() => {
    switch (fClase) {
      case 'renovada':     return conFiltros.filter(r => r.clase === 'renovada');
      case 'pendiente':    return pend;
      case 'en_gestion':   return pend.filter(r => r.gestionada);
      case 'sin_gestionar':return pend.filter(r => !r.gestionada);
      case 'no_renovada':  return conFiltros.filter(r => r.clase === 'no_renovada');
      default:             return conFiltros;
    }
  }, [fClase, conFiltros, pend]);

  // Exporta a Excel la lista visible, con las mismas columnas y los filtros aplicados.
  const exportarExcel = async () => {
    try {
      const XLSX = await loadXLSX();
      const n = v => Math.round((Number(v) || 0) * 100) / 100;
      const cols = [
        ['Número',      r => r.numero || `#${r.id}`],
        ['Cliente',     r => r.cliente_nombre || ''],
        ['Ramo',        r => r.ramo_nombre || ''],
        ['Aseguradora', r => r.aseguradora_nombre || ''],
        ['Ejecutivo',   r => r.ejecutivo_nombre || ''],
        ['Vencimiento', r => fmtFecha(r.fecha_vencimiento) || ''],
        ['Prima',       r => n(r.prima), true],
        ['Estado',      r => CLASES[r.clase]?.label || r.clase || ''],
        ['Gestión',     r => r.clase === 'pendiente' ? (r.gestionada ? 'En gestión' : 'Sin gestionar') : ''],
      ];
      const filtros = [];
      if (fEjec)   filtros.push('Ejecutivo: ' + (ejecutivos.find(x => String(x.id) === String(fEjec))?.nombre || ''));
      if (fAgente) filtros.push('Agente: ' + (agentes.find(a => String(a.id) === String(fAgente))?.nombre || ''));
      if (fAseg)   filtros.push('Aseguradora: ' + (aseguradoras.find(a => String(a.id) === String(fAseg))?.nombre || ''));
      if (fRamo)   filtros.push('Ramo: ' + (ramos.find(r => String(r.id) === String(fRamo))?.nombre || ''));
      if (fClase)  filtros.push('Ver: ' + ({ renovada:'Renovadas', pendiente:'Pendientes', en_gestion:'En gestión', sin_gestionar:'Sin gestionar', no_renovada:'No renovadas' }[fClase] || fClase));

      const aoa = [
        ['RIESGO UNIDO — Renovaciones (avance del período)'],
        [`Período: ${fmtFecha(desde)} a ${fmtFecha(hasta)}`],
        [`Avance: ${pct}% — ${nRenov} renovada(s) de ${tot} · ${nPend} pendiente(s) (${nGest} en gestión, ${nSinGest} sin gestionar) · ${nNo} no renovada(s)`],
        [filtros.join('   ·   ')],
        [`${visibles.length} registro(s) en la lista`],
        [],
        cols.map(c => c[0]),
      ];
      let totalPrima = 0;
      for (const r of visibles) {
        aoa.push(cols.map(c => { const v = c[1](r); if (c[2]) totalPrima += Number(v) || 0; return v; }));
      }
      aoa.push([]);
      const totalRow = cols.map(() => '');
      totalRow[0] = 'TOTAL';
      totalRow[6] = n(totalPrima);
      aoa.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{wch:18},{wch:28},{wch:18},{wch:22},{wch:16},{wch:14},{wch:12},{wch:20},{wch:14}];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Renovaciones');
      XLSX.writeFile(wb, `Renovaciones_${desde}_a_${hasta}.xlsx`);
    } catch (e) {
      alert('No se pudo generar el Excel: ' + (e.message || ''));
    }
  };

  const chip = (v, label, count, color) => (
    <button onClick={() => setFClase(fClase === v ? '' : v)} style={{
      padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)',
      border: `1px solid ${fClase === v ? color : 'rgba(143,168,192,0.25)'}`,
      background: fClase === v ? color : 'transparent',
      color: fClase === v ? '#fff' : 'var(--silver)', fontWeight: 600,
    }}>{label} · {count}</button>
  );

  const selBox = { display: 'flex', flexDirection: 'column', gap: 3 };
  const selLabel = { fontSize: 11, color: 'var(--silver)', paddingLeft: 2 };

  const setMesPreset = (offset) => {
    const d = new Date();
    const y = d.getFullYear(), m = d.getMonth() + offset;
    const p2 = (n) => String(n).padStart(2, '0');
    const ini = new Date(y, m, 1), fin = new Date(y, m + 1, 0);
    setDesde(`${ini.getFullYear()}-${p2(ini.getMonth() + 1)}-01`);
    setHasta(`${fin.getFullYear()}-${p2(fin.getMonth() + 1)}-${p2(fin.getDate())}`);
  };

  return (
    <div>
      {/* Período + tarjeta de avance */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 16 }}>
        <div className="card card-sm" style={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--silver)', letterSpacing: 1 }}>AVANCE DE RENOVACIÓN — MI CARTERA</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={selBox}><span style={selLabel}>Desde</span>
                <input className="form-input" type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ width: 140 }} /></div>
              <div style={selBox}><span style={selLabel}>Hasta</span>
                <input className="form-input" type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ width: 140 }} /></div>
              <button className="btn btn-secondary btn-sm" onClick={() => setMesPreset(-1)} title="Fijar al mes anterior">Mes anterior</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setMesPreset(0)} title="Fijar al mes en curso">Este mes</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setMesPreset(1)} title="Fijar al próximo mes">Próximo mes</button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontSize: 44, fontWeight: 800, color: 'var(--sky)', lineHeight: 1 }}>{pct}%</span>
            <span style={{ fontSize: 13, color: 'var(--silver)' }}>{nRenov} de {tot} renovada{tot !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ height: 10, borderRadius: 6, background: 'rgba(var(--silver-rgb),0.18)', overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: `${tot ? (nRenov / tot) * 100 : 0}%`, background: '#22c55e' }} />
            <div style={{ width: `${tot ? (nPend / tot) * 100 : 0}%`, background: '#F59E0B' }} />
            <div style={{ width: `${tot ? (nNo / tot) * 100 : 0}%`, background: '#ef4444' }} />
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--silver)', flexWrap: 'wrap' }}>
            <span><span style={{ color: 'var(--text-success)', fontWeight: 700 }}>●</span> {nRenov} renovadas</span>
            <span><span style={{ color: 'var(--text-warning)', fontWeight: 700 }}>●</span> {nPend} pendientes <span style={{ opacity: 0.8 }}>({nGest} en gestión · {nSinGest} sin gestionar)</span></span>
            <span><span style={{ color: 'var(--text-danger)', fontWeight: 700 }}>●</span> {nNo} no renovadas</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {chip('', 'Todas', tot, 'var(--sky)')}
          {chip('renovada', 'Renovadas', nRenov, CLASES.renovada.color)}
          {chip('pendiente', 'Pendientes', nPend, CLASES.pendiente.color)}
          {chip('en_gestion', 'En gestión', nGest, 'var(--sky-light)')}
          {chip('sin_gestionar', 'Sin gestionar', nSinGest, 'var(--silver)')}
          {chip('no_renovada', 'No renovadas', nNo, CLASES.no_renovada.color)}
        </div>
        {puedeFiltrarCartera && (<>
          <div style={selBox}>
            <span style={selLabel}>Ejecutivo</span>
            <select className="form-select" style={{ width: 150 }} value={fEjec} onChange={e => setFEjec(e.target.value)}>
              <option value="">Todos</option>
              {ejecutivos.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
            </select>
          </div>
          <div style={selBox}>
            <span style={selLabel}>Agente</span>
            <select className="form-select" style={{ width: 150 }} value={fAgente} onChange={e => setFAgente(e.target.value)}>
              <option value="">Todos</option>
              {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
        </>)}
        <div style={selBox}>
          <span style={selLabel}>Aseguradora</span>
          <select className="form-select" style={{ width: 150 }} value={fAseg} onChange={e => setFAseg(e.target.value)}>
            <option value="">Todas</option>
            {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
        <div style={selBox}>
          <span style={selLabel}>Ramo</span>
          <select className="form-select" style={{ width: 140 }} value={fRamo} onChange={e => setFRamo(e.target.value)}>
            <option value="">Todos</option>
            {ramos.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
        </div>
        {(fClase || fAseg || fRamo || fEjec || fAgente) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFClase(''); setFAseg(''); setFRamo(''); setFEjec(''); setFAgente(''); }}>✕ Limpiar</button>
        )}
        <button className="btn btn-secondary" style={{ marginLeft:'auto' }} onClick={exportarExcel} disabled={visibles.length === 0}
          title="Descarga a Excel la lista de renovaciones tal como se ve, con el período y los filtros aplicados">
          <FileText size={15}/> Descargar Excel
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--silver)', marginBottom: 6, paddingLeft: 2 }}>
        {visibles.length} póliza{visibles.length !== 1 ? 's' : ''}{loading ? ' · cargando…' : ''}
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr>
            <th>Número</th><th>Cliente</th><th>Ramo</th><th>Aseguradora</th><th>Ejecutivo</th>
            <th>Vencimiento</th><th>Prima</th><th>Estado</th><th>Gestión</th><th></th>
          </tr></thead>
          <tbody>
            {visibles.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--silver)', padding: 20 }}>
                {loading ? 'Cargando…' : 'No hay pólizas que venzan en este período con estos filtros.'}
              </td></tr>
            ) : visibles.map(r => {
              const c = CLASES[r.clase];
              return (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{r.numero || `#${r.id}`}</td>
                  <td>{r.cliente_nombre}</td>
                  <td>{r.ramo_nombre}</td>
                  <td>{r.aseguradora_nombre}</td>
                  <td>{r.ejecutivo_nombre || '—'}</td>
                  <td>{fmtFecha(r.fecha_vencimiento)}</td>
                  <td>{fmt$(r.prima)}</td>
                  <td><span style={{ padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: c.color, background: c.bg }}>{c.label}</span></td>
                  <td>
                    {r.clase === 'pendiente'
                      ? (r.gestionada
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--sky-light)' }}>● En gestión</span>
                          : <span style={{ fontSize: 11, color: 'var(--silver)' }}>Sin gestionar</span>)
                      : <span style={{ fontSize: 11, color: 'var(--silver)' }}>—</span>}
                  </td>
                  <td>
                    {onVerPoliza && <button className="btn btn-secondary btn-sm" onClick={() => onVerPoliza(r.id)}>Ver</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
